<template>
  <div class="driver-order-page">
    <div class="header">
      <div class="back-btn" @click="$router.back()">← {{ $t('common.back') }}</div>
      <div class="header-title">🛒 {{ $t('driverOnBehalf.title') }}</div>
    </div>

    <div class="content">
      <!-- ① 选择批发商 -->
      <div class="section">
        <div class="section-title">{{ $t('driverOnBehalf.selectWholesaler') }}</div>
        <div class="search-box">
          <input v-model="wholesalerSearch" type="text" :placeholder="$t('driverOnBehalf.searchWholesaler')" @input="filterWholesalers" class="search-input" />
        </div>
        <div class="wholesaler-list">
          <div v-for="w in displayedWholesalers" :key="w.id" :class="['wholesaler-selector', { selected: selectedWholesaler && selectedWholesaler.id === w.id }]" @click="selectWholesaler(w)">
            <div class="wholesaler-name">{{ w.companyName || w.name }}</div>
            <div class="wholesaler-info">
              <span>📞 {{ w.contactPerson || w.contactPhone }}</span>
              <span v-if="w.address"> · 📍 {{ w.address }}</span>
            </div>
          </div>
          <div v-if="displayedWholesalers.length === 0" class="empty-state">{{ $t('driverOnBehalf.noWholesalers') }}</div>
        </div>
      </div>

      <!-- ② 选择档口（选完批发商后显示） -->
      <div class="section" v-if="selectedWholesaler">
        <div class="section-title">
          {{ $t('driverOnBehalf.selectStall') }}
          <button class="create-stall-btn" @click.stop="showCreateStallDialog = true">+ {{ $t('driverOnBehalf.createNewStall') }}</button>
        </div>
        <div class="search-box">
          <input v-model="stallSearch" type="text" :placeholder="$t('driverOnBehalf.searchStall')" @input="filterStalls" class="search-input" />
        </div>
        <div class="stall-list">
          <div v-for="s in displayedStalls" :key="s.id" :class="['stall-selector', { selected: selectedCustomer && selectedCustomer.id === s.id }]" @click="selectStall(s)">
            <div class="stall-name">{{ s.shopName || s.customerName || s.name }}</div>
            <div class="stall-info">
              <span>📞 {{ s.contactPerson || s.phone }}</span>
              <span v-if="s.regionName"> · 📍 {{ s.regionName }}</span>
            </div>
          </div>
          <div v-if="displayedStalls.length === 0 && !stallsLoading" class="empty-state">{{ $t('driverOnBehalf.noStalls') }}</div>
          <div v-if="stallsLoading" class="loading-state">
            <div class="loading-spinner">⏳</div>
            <p>{{ $t('driverOnBehalf.loadingStalls') }}</p>
          </div>
        </div>
        <div v-if="stallTotalPages > 1" class="pagination-bar">
          <button class="page-btn" :disabled="stallPage <= 1" @click="goStallPage(stallPage - 1)">‹ {{ $t('driverOnBehalf.prevPage') }}</button>
          <span class="page-info">{{ stallPage }} / {{ stallTotalPages }} {{ $t('driverOnBehalf.pageUnit') }}, {{ $t('driverOnBehalf.totalUnit') }} {{ stallTotal }} {{ $t('driverOnBehalf.itemUnit') }}</span>
          <button class="page-btn" :disabled="stallPage >= stallTotalPages" @click="goStallPage(stallPage + 1)">{{ $t('driverOnBehalf.nextPage') }} ›</button>
        </div>
      </div>

      <!-- 创建档口弹窗 -->
      <div v-if="showCreateStallDialog" class="dialog-overlay" @click.self="showCreateStallDialog = false">
        <div class="dialog-box">
          <div class="dialog-title">{{ $t('driverOnBehalf.createNewStall') }}</div>
          <div class="dialog-content">
            <input v-model="newStallName" type="text" :placeholder="$t('driverOnBehalf.enterStallName')" class="dialog-input" @keyup.enter="createStall" ref="stallNameInput" />
          </div>
          <div class="dialog-actions">
            <button class="dialog-cancel-btn" @click="showCreateStallDialog = false">{{ $t('driverOnBehalf.cancel') }}</button>
            <button class="dialog-confirm-btn" @click="createStall" :disabled="!newStallName.trim() || creatingStall">
              {{ creatingStall ? $t('driverOnBehalf.creating') : $t('driverOnBehalf.confirm') }}
            </button>
          </div>
        </div>
      </div>

      <!-- ③ 选择商品（选完档口后显示） -->
      <div class="section" v-if="selectedCustomer">
        <div class="section-title">{{ $t('driverOnBehalf.selectProduct') }}</div>
        <!-- 商品搜索（带下拉） -->
        <div class="search-box" style="position: relative;">
          <input v-model="productSearch" type="text" :placeholder="$t('driverOnBehalf.searchProduct')" @input="onProductSearch" @focus="onProductSearch" class="search-input" />
          <div v-if="showProductDropdown && productSearchResults.length > 0" class="product-dropdown">
            <div v-for="p in productSearchResults" :key="'search-' + p.id" class="product-option" @click="selectSearchProduct(p)">
              <span class="option-name">{{ p.name }}</span>
              <span class="option-sku">{{ p.sku || '-' }}</span>
            </div>
          </div>
        </div>
        <!-- 已选商品区域 -->
        <div v-if="selectedProducts.length > 0" class="selected-products-section">
          <div class="selected-title">已选商品 ({{ selectedProducts.length }})</div>
          <div class="selected-products-list">
            <div v-for="product in selectedProducts" :key="'selected-' + product.id" class="selected-product-item">
              <div class="selected-product-info">
                <span class="selected-product-name">{{ product.name }}</span>
                <span class="selected-product-price">S$ {{ (prices[product.id] || 0).toFixed(2) }} × {{ quantities[product.id] || 0 }}{{ product.unit || 'kg' }}</span>
              </div>
              <button class="btn-remove-selected" @click="toggleProductSelection(product)">✕</button>
            </div>
          </div>
        </div>
        <div class="select-all-bar">
          <label class="select-all-label">
            <input type="checkbox" :checked="allSelected" @change="toggleAllSelection" class="select-all-cb" />
            {{ $t('driverOnBehalf.selectAll') }}
          </label>
          <span class="select-count">{{ $t('driverOnBehalf.selectedCount') }} {{ selectedProductIds.length }} / {{ displayedProducts.length }}</span>
        </div>
        <div v-if="productsLoading" class="loading-state">
          <div class="loading-spinner">⏳</div>
          <p>{{ $t('driverOnBehalf.loadingProducts') }}</p>
        </div>
        <div v-else class="product-list">
          <div v-for="product in displayedProducts" :key="product.id" class="product-item">
            <input type="checkbox" :checked="selectedProductIds.includes(product.id)" @change="toggleProductSelection(product)" class="product-checkbox" />
            <div class="product-emoji">{{ product.emoji || '📦' }}</div>
            <div class="product-details">
              <div class="product-name">{{ product.name }}</div>
              <div class="product-stock">
                📦 {{ $t('driverOnBehalf.stock') }}: <span :class="{ 'stock-low': availableStock(product) <= 0, 'stock-warn': availableStock(product) > 0 && availableStock(product) <= 10 }">{{ availableStock(product).toFixed(1) }}</span> {{ product.unit || 'kg' }}
              </div>
              <div class="product-price-ref">
                <template v-if="product.wholesalePrice != null && product.wholesalePrice > 0">
                  <span class="ref-price">{{ $t('driverOnBehalf.wholesalePrice') }} S$ {{ Number(product.wholesalePrice).toFixed(2) }}</span>
                  <span class="market-ref">{{ $t('driverOnBehalf.marketPrice') }} S$ {{ (product.marketPrice || 0).toFixed(2) }}</span>
                </template>
                <template v-else>
                  <span class="ref-price">S$ {{ (product.marketPrice || 0).toFixed(2) }}</span>
                </template>
              </div>
              <div class="price-edit-box" @click="openPricePicker(product)" :class="{ disabled: !selectedProductIds.includes(product.id) }">
                <span class="price-label">💰 单价</span>
                <div class="price-value-row">
                  <span class="price-prefix">S$</span>
                  <span class="price-value">{{ prices[product.id] ? Number(prices[product.id]).toFixed(2) : '0.00' }}</span>
                  <span class="unit-label">/ {{ product.unit || 'kg' }}</span>
                  <span class="edit-icon">✏️</span>
                </div>
                <div class="price-ref-small" v-if="prices[product.id] && Math.abs(Number(prices[product.id]) - Number((product.wholesalePrice || product.marketPrice) || 0)) > 0.01">参考价: S$ {{ Number((product.wholesalePrice || product.marketPrice) || 0).toFixed(2) }}</div>
              </div>
              <div class="quantity-edit-box" @click="openQuantityPicker(product)" :class="{ disabled: !selectedProductIds.includes(product.id) }">
                <span class="qty-label">📦 数量</span>
                <div class="qty-value-row">
                  <span class="qty-value">{{ quantities[product.id] || 0 }}</span>
                  <span class="unit">{{ product.unit || 'kg' }}</span>
                  <span class="edit-icon">✏️</span>
                </div>
              </div>
            </div>
          </div>
          <div v-if="displayedProducts.length === 0" class="empty-state">{{ $t('driverOnBehalf.noProducts') }}</div>
        </div>
        <!-- 商品分页 -->
        <div v-if="productTotalPages > 1" class="pagination-bar">
          <button class="page-btn" :disabled="productPage <= 0" @click="goProductPage(productPage - 1)">‹ {{ $t('driverOnBehalf.prevPage') }}</button>
          <span class="page-info">{{ productPage + 1 }} / {{ productTotalPages }} {{ $t('driverOnBehalf.pageUnit') }}, {{ $t('driverOnBehalf.totalUnit') }} {{ productTotal }} {{ $t('driverOnBehalf.itemUnit') }}</span>
          <button class="page-btn" :disabled="productPage >= productTotalPages - 1" @click="goProductPage(productPage + 1)">{{ $t('driverOnBehalf.nextPage') }} ›</button>
        </div>
      </div>

      <!-- ④ 收款信息 -->
      <div class="section payment-section" v-if="selectedWholesaler">
        <div class="section-title">💳 {{ $t('driverOnBehalf.paymentInfo') }}</div>
        <div class="payment-hint">{{ $t('driverOnBehalf.paymentHint') }}</div>
        <div class="section-subtitle">{{ $t('driverOnBehalf.selectPaymentMethod') }}</div>
        <div class="payment-options">
          <label v-for="payment in paymentOptions" :key="payment.value" :class="['payment-option', { selected: paymentMethod === payment.value }]">
            <input type="radio" :value="payment.value" v-model="paymentMethod" />
            <span>{{ payment.icon }} {{ payment.label }}</span>
          </label>
        </div>
        <div v-if="paymentMethod === 'PAYNOW'" class="payment-detail-box">
          <div class="payment-detail-title">📱 {{ $t('driverOnBehalf.scanToPay') }}</div>
          <div v-if="paymentConfig.paynowQrUrl" class="qr-display">
            <img :src="paymentConfig.paynowQrUrl" alt="收款二维码" class="qr-image" />
            <p class="qr-hint">{{ $t('driverOnBehalf.scanHint') }}</p>
          </div>
          <div v-else class="qr-empty"><p>⚠️ {{ $t('driverOnBehalf.noQrConfigured') }}</p></div>
        </div>
        <div>
          <div class="section-subtitle" style="margin-top:12px;">{{ $t('driverOnBehalf.uploadProofOptional') }}</div>
          <div class="proof-upload-area" @click="triggerProofUpload">
            <img v-if="paymentProofUrl" :src="paymentProofUrl" class="proof-preview" />
            <div v-else class="proof-placeholder">
              <span class="proof-icon">📎</span>
              <p>{{ $t('driverOnBehalf.uploadProof') }}</p>
            </div>
          </div>
          <input ref="proofInput" type="file" accept="image/*" style="display:none" @change="handleProofUpload" />
        </div>
      </div>

      <!-- 订单金额预览 -->
      <div v-if="orderSummary.total > 0" class="section summary-section">
        <div class="section-title">📋 {{ $t('driverOnBehalf.orderSummaryTitle') }}</div>
        <div v-for="item in orderItems" :key="item.productId" class="line-item-summary">
          <span class="line-name">{{ item.productName }}</span>
          <span class="line-qty">×{{ item.qty }}</span>
          <span class="line-price">S$ {{ item.price.toFixed(2) }}</span>
          <span class="line-tax" v-if="item.lineTax > 0">Tax S$ {{ item.lineTax.toFixed(2) }}</span>
        </div>
        <div class="summary-row"><span class="label">{{ $t('driverOnBehalf.subtotal') }}</span><span class="value">S$ {{ orderSummary.subtotal.toFixed(2) }}</span></div>
        <div class="summary-row"><span class="label">{{ $t('driverOnBehalf.gstTax') }}</span><span class="value">S$ {{ orderSummary.gst.toFixed(2) }}</span></div>
        <div class="summary-row total"><span class="label">{{ $t('driverOnBehalf.totalPayable') }}</span><span class="value">S$ {{ orderSummary.total.toFixed(2) }}</span></div>
      </div>

      <!-- 校验错误提示 -->
      <div v-if="validationErrors.length > 0" class="section error-section">
        <div class="section-title">❌ {{ $t('driverOnBehalf.validationFailed') }}</div>
        <div v-for="(err, idx) in validationErrors" :key="idx" class="error-item">{{ err }}</div>
      </div>

      <!-- 提交按钮 -->
      <div class="footer" v-if="selectedWholesaler">
        <button class="submit-btn" @click="submitOrder(false)" :disabled="submitting || orderItems.length === 0">{{ submitting ? $t('driverOnBehalf.submitting') : $t('driverOnBehalf.submit') }}</button>
        <button class="submit-continue-btn" @click="submitOrder(true)" :disabled="submitting || orderItems.length === 0">{{ submitting ? $t('driverOnBehalf.submitting') : $t('driverOnBehalf.submitContinue') }}</button>
      </div>
    </div>

    <!-- 数量选择弹窗 -->
    <div v-if="showQuantityPicker" class="dialog-overlay" @click.self="closeQuantityPicker">
      <div class="dialog-box picker-dialog">
        <div class="dialog-title">📦 数量 {{ currentPickerProduct && currentPickerProduct.name ? currentPickerProduct.name : '' }}</div>
        <div class="dialog-content">
          <div class="quantity-picker">
            <div class="picker-highlight"></div>
            <div class="picker-scroll" ref="modalPickerScroll" @scroll="onModalPickerScroll">
              <div class="picker-spacer"></div>
              <div class="picker-item" v-for="qty in getQuantityOptions()" :key="qty">{{ qty }}</div>
              <div class="picker-spacer"></div>
            </div>
          </div>
          <div class="custom-input-row">
            <label>自定义：</label>
            <input type="number" v-model="customQuantity" min="0.5" step="0.5" class="custom-input" @input="onCustomQuantityInput" />
            <span class="input-unit">{{ currentPickerProduct && currentPickerProduct.unit ? currentPickerProduct.unit : 'kg' }}</span>
          </div>
        </div>
        <div class="dialog-actions">
          <button class="dialog-cancel-btn" @click="closeQuantityPicker">取消</button>
          <button class="dialog-confirm-btn" @click="confirmQuantity">确定</button>
        </div>
      </div>
    </div>

    <!-- 价格选择弹窗 -->
    <div v-if="showPricePicker" class="dialog-overlay" @click.self="closePricePicker">
      <div class="dialog-box picker-dialog">
        <div class="dialog-title">💰 价格 {{ currentPickerProduct && currentPickerProduct.name ? currentPickerProduct.name : '' }}</div>
        <div class="dialog-content">
          <div class="quantity-picker">
            <div class="picker-highlight"></div>
            <div class="picker-scroll" ref="pricePickerScroll" @scroll="onPricePickerScroll">
              <div class="picker-spacer"></div>
              <div class="picker-item" v-for="price in getPriceOptions()" :key="price">{{ Number(price).toFixed(2) }}</div>
              <div class="picker-spacer"></div>
            </div>
          </div>
          <div class="custom-input-row">
            <label>自定义：</label>
            <input type="number" v-model="customPrice" min="0" step="0.01" class="custom-input" @input="onCustomPriceInput" />
            <span class="input-unit">S$</span>
          </div>
          <div class="price-ref-box">
            <span>参考价：</span>
            <span class="price-ref-val">S$ {{ currentPickerProduct ? Number((currentPickerProduct.wholesalePrice || currentPickerProduct.marketPrice) || 0).toFixed(2) : '0.00' }}</span>
          </div>
        </div>
        <div class="dialog-actions">
          <button class="dialog-cancel-btn" @click="closePricePicker">取消</button>
          <button class="dialog-confirm-btn" @click="confirmPrice">确定</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'DriverOrderOnBehalf',
  data() {
    return {
      selectedWholesaler: null,
      wholesalerOptions: [],
      wholesalerSearch: '',
      
      // 档口相关
      selectedCustomer: null,
      stallOptions: [],
      stallSearch: '',
      stallPage: 1,
      stallPageSize: 20,
      stallTotal: 0,
      stallTotalPages: 0,
      stallsLoading: false,
      showCreateStallDialog: false,
      newStallName: '',
      creatingStall: false,
      
      products: [],
      productSearch: '',
      quantities: {},
      prices: {},
      selectedProductIds: [],
      // 商品分页
      productPage: 0,
      productPageSize: 20,
      productTotal: 0,
      productTotalPages: 0,
      // 商品搜索下拉
      showProductDropdown: false,
      productSearchResults: [],
      productSearchTimer: null,
      paymentMethod: 'CASH',
      paymentOptions: [],
      paymentProofUrl: '',
      submitting: false,
      productsLoading: false,
      validationErrors: [],
      paymentConfig: { paynowQrUrl: '', bankName: '', bankAccountName: '', bankAccountNumber: '' },
      
      // 数量选择弹窗
      showQuantityPicker: false,
      currentPickerProduct: null,
      customQuantity: 1,
      
      // 价格选择弹窗
      showPricePicker: false,
      customPrice: 0,
      
      // 改价标记
      originalPrices: {},
      priceChanged: {}
    };
  },
  computed: {
    displayedWholesalers() {
      return this.wholesalerOptions;
    },
    displayedStalls() {
      return this.stallOptions;
    },
    displayedProducts() {
      if (!this.productSearch.trim()) {
        return this.products;
      }
      return this.productSearchResults;
    },
    selectedProducts() {
      return this.products.filter(p => this.selectedProductIds.includes(p.id));
    },
    orderItems() {
      return this.selectedProductIds
        .filter(id => this.quantities[id] && this.quantities[id] > 0)
        .map(id => {
          const p = this.products.find(x => x.id === id);
          const price = this.prices[id] || p?.marketPrice || 0;
          const taxRate = p?.taxRate || 0;
          const qty = this.quantities[id];
          const priceChanged = this.priceChanged[id] || false;
          // ✅ 逐个商品先四舍五入再累加，与后端保持一致
          const lineSubtotal = Number((price * qty).toFixed(2));
          const lineTax = Number((lineSubtotal * taxRate).toFixed(2));
          return { productId: p.id, productName: p.name, price, qty, taxRate, lineSubtotal, lineTax, priceChanged };
        });
    },
    allSelected() {
      return this.displayedProducts.length > 0 && this.displayedProducts.every(p => this.selectedProductIds.includes(p.id));
    },
    orderSummary() {
      const items = this.orderItems;
      const subtotal = Number(items.reduce((s, i) => s + i.lineSubtotal, 0).toFixed(2));
      const gst = Number(items.reduce((s, i) => s + i.lineTax, 0).toFixed(2));
      const total = Number((subtotal + gst).toFixed(2));
      return { subtotal, gst, total };
    }
  },
  watch: {
    selectedWholesaler: {
      async handler(w) {
        if (w && w.id) {
          await this.loadPaymentConfig(w.id);
          await this.fetchStalls(); // 加载档口列表
        } else {
          this.paymentConfig = { paynowQrUrl: '', bankName: '', bankAccountName: '', bankAccountNumber: '' };
          this.selectedCustomer = null;
          this.stallOptions = [];
        }
      }
    }
  },
  created() {
    this.paymentOptions = [
      { label: this.$t('driverOnBehalf.cash'), value: 'CASH', icon: '💵' },
      { label: this.$t('driverOnBehalf.paynow'), value: 'PAYNOW', icon: '📱' },
      { label: this.$t('driverOnBehalf.bankTransfer'), value: 'BANK_TRANSFER', icon: '🏦' },
      { label: this.$t('driverOnBehalf.creditCard'), value: 'CREDIT_CARD', icon: '💳' },
    ];
    this.fetchWholesalers();
  },
  methods: {
    async fetchWholesalers() {
      try {
        // 调用配送员专用接口，只返回当前用户关联的批发商
        const res = await api.get('/driver/wholesalers');
        const items = Array.isArray(res) ? res : (res.content || res.items || res.data || []);
        this.wholesalerOptions = items;
      } catch (e) { 
        console.error(this.$t('driverOnBehalf.loadWholesalersFailed'), e);
        this.wholesalerOptions = [];
      }
    },
    async fetchStalls(page = 1) {
      if (this.stallsLoading) return;
      this.stallsLoading = true;
      
      try {
        const params = {
          page: page,
          size: this.stallPageSize,
          wholesalerId: this.selectedWholesaler.id
        };
        if (this.stallSearch && this.stallSearch.trim()) {
          params.keyword = this.stallSearch.trim();
        }
        
        const res = await api.get('/wholesaler/stalls', { params });
        if (res && res.items) {
          this.stallOptions = res.items.map(c => {
            let customerId = c.stallId || c.id;
            if (typeof customerId === 'object' && customerId !== null) {
              customerId = customerId.id || customerId.value || Object.values(customerId)[0];
            }
            return {
              ...c,
              id: Number(customerId)
            };
          });
          this.stallPage = page;
          this.stallTotal = res.total || 0;
          this.stallTotalPages = res.totalPages || 0;
        }
      } catch (error) {
        console.error('加载档口失败:', error);
        this.stallOptions = [];
      } finally {
        this.stallsLoading = false;
      }
    },
    filterStalls() {
      this.fetchStalls(1);
    },
    goStallPage(page) {
      if (page < 1 || page > this.stallTotalPages) return;
      this.fetchStalls(page);
    },
    selectStall(s) {
      if (this.selectedCustomer && this.selectedCustomer.id === s.id) {
        this.selectedCustomer = null;
        this.products = [];
        this.productSearch = '';
        this.selectedProductIds = [];
        this.quantities = {};
        this.prices = {};
        return;
      }
      this.selectedCustomer = s;
      this.loadProducts();
    },
    async createStall() {
      if (!this.newStallName.trim()) return;
      this.creatingStall = true;
      try {
        await api.post('/wholesaler/stalls', {
          wholesalerId: this.selectedWholesaler.id,
          stallName: this.newStallName.trim()
        });
        this.showCreateStallDialog = false;
        this.newStallName = '';
        await this.fetchStalls();
      } catch (error) {
        console.error('创建档口失败:', error);
        alert('创建档口失败: ' + (error.message || '未知错误'));
      } finally {
        this.creatingStall = false;
      }
    },
    async loadProducts(page = 0) {
      this.productsLoading = true;
      try {
        const res = await api.get('/customer/products/for-order-on-behalf', {
          params: { 
            wholesalerId: this.selectedWholesaler.id,
            customerId: this.selectedCustomer.id,
            page: page,
            size: this.productPageSize
          }
        });
        let items = [];
        let total = 0;
        let totalPages = 0;
        if (res.items) {
          items = res.items;
          total = res.total || 0;
          totalPages = res.totalPages || 0;
        } else if (res.data && res.data.items) {
          items = res.data.items;
          total = res.data.total || 0;
          totalPages = res.data.totalPages || 0;
        } else if (Array.isArray(res)) {
          items = res;
        }
        this.products = items.map(item => ({ ...item, id: Number(item.id), quantity: 0 }));
        this.productPage = page;
        this.productTotal = total;
        this.productTotalPages = totalPages;
      } catch (e) { 
        console.error(this.$t('driverOnBehalf.loadProductsFailed'), e); 
        this.products = []; 
      } finally { 
        this.productsLoading = false; 
      }
    },
    goProductPage(page) {
      if (page < 0 || page >= this.productTotalPages) return;
      this.loadProducts(page);
    },
    onProductSearch() {
      clearTimeout(this.productSearchTimer);
      this.productSearchTimer = setTimeout(async () => {
        const keyword = this.productSearch.trim();
        if (!keyword) {
          this.productSearchResults = [];
          this.showProductDropdown = false;
          return;
        }
        try {
          const res = await api.get('/customer/products/for-order-on-behalf', {
            params: {
              wholesalerId: this.selectedWholesaler.id,
              customerId: this.selectedCustomer.id,
              keyword
            }
          });
          let items = [];
          if (res.items) {
            items = res.items;
          } else if (res.data && res.data.items) {
            items = res.data.items;
          } else if (Array.isArray(res)) {
            items = res;
          }
          this.productSearchResults = items.map(item => ({ ...item, id: Number(item.id) }));
          this.showProductDropdown = this.productSearchResults.length > 0;
        } catch (e) {
          console.error('商品搜索失败:', e);
        }
      }, 300);
    },
    selectSearchProduct(product) {
      const index = this.selectedProductIds.indexOf(product.id);
      if (index === -1) {
        this.toggleProductSelection(product);
      }
      this.showProductDropdown = false;
      // 把搜索结果合并到主列表
      const exists = this.products.find(p => p.id === product.id);
      if (!exists) {
        this.products.push({ ...product, quantity: 0 });
      }
      this.productSearch = '';
      this.productSearchResults = [];
    },
    filterWholesalers() {},
    async selectWholesaler(w) {
      if (this.selectedWholesaler && this.selectedWholesaler.id === w.id) {
        // 取消选择
        this.selectedWholesaler = null;
        this.selectedCustomer = null;
        this.products = [];
        this.productSearch = '';
        this.selectedProductIds = [];
        this.quantities = {};
        this.validationErrors = [];
        return;
      }
      this.selectedWholesaler = w;
      this.selectedCustomer = null;
      this.products = [];
      this.productSearch = '';
      this.selectedProductIds = [];
      this.quantities = {};
      this.prices = {};
      this.validationErrors = [];
      // 选择批发商后会自动触发 watch 加载档口列表
    },
    filterProducts() {},
    availableStock(product) {
      const totalStock = product.totalStock || 0;
      const reserved = product.reservedQty || 0;
      return Math.max(0, totalStock - reserved);
    },
    clampQuantity(product) {
      const max = this.availableStock(product);
      let qty = this.quantities[product.id] || 0;
      if (qty > max) {
        qty = max;
        if (max <= 0) this.$message.warning(`${product.name} ${this.$t('driverOnBehalf.stockInsufficient')}`);
        else this.$message.warning(`${product.name} ${this.$t('driverOnBehalf.maxOrderable')} ${max} ${product.unit || 'kg'}`);
      }
      if (qty < 0) qty = 0;
      this.$set(this.quantities, product.id, qty);
    },
    onQtyInput(product, event) {
      const val = parseFloat(event.target.value);
      this.$set(this.quantities, product.id, isNaN(val) || val < 0 ? 0 : val);
      this.clampQuantity(product);
    },
    toggleProductSelection(product) {
      const idx = this.selectedProductIds.indexOf(product.id);
      if (idx > -1) {
        this.selectedProductIds.splice(idx, 1);
        this.$set(this.quantities, product.id, 0);
        this.$set(this.prices, product.id, 0);
        this.$set(this.originalPrices, product.id, 0);
        this.$set(this.priceChanged, product.id, false);
      } else {
        this.selectedProductIds.push(product.id);
        const maxStock = this.availableStock(product);
        this.$set(this.quantities, product.id, Math.min(product.minOrderQuantity || 1, maxStock > 0 ? maxStock : product.minOrderQuantity || 1));
        // 每次都重新初始化价格
        const originalPrice = (product.wholesalePrice != null && product.wholesalePrice > 0) ? product.wholesalePrice : (product.marketPrice || 0);
        this.$set(this.prices, product.id, originalPrice);
        this.$set(this.originalPrices, product.id, originalPrice);
        this.$set(this.priceChanged, product.id, false);
      }
    },
    toggleAllSelection() {
      if (this.allSelected) {
        this.selectedProductIds = [];
        this.displayedProducts.forEach(p => { this.$set(this.quantities, p.id, 0); this.$set(this.prices, p.id, 0); });
      } else {
        this.selectedProductIds = this.displayedProducts.map(p => p.id);
        this.displayedProducts.forEach(p => {
          const maxStock = this.availableStock(p);
          this.$set(this.quantities, p.id, Math.min(p.minOrderQuantity || 1, maxStock > 0 ? maxStock : p.minOrderQuantity || 1));
          if (!this.prices[p.id]) {
            this.$set(this.prices, p.id, (p.wholesalePrice != null && p.wholesalePrice > 0) ? p.wholesalePrice : (p.marketPrice || 0));
          }
        });
      }
    },
    increaseQuantity(product) {
      const max = this.availableStock(product);
      let qty = this.quantities[product.id] || 0;
      if (qty < max) { this.$set(this.quantities, product.id, qty + 0.5); }
      else { this.$message.warning(`${product.name} ${this.$t('driverOnBehalf.stockReached')}`); }
    },
    decreaseQuantity(product) {
      let qty = this.quantities[product.id];
      if (!qty) return;
      const m = product.minOrderQuantity || 1;
      if (qty > m) { this.$set(this.quantities, product.id, qty - 0.5); }
    },
    
    // 数量选择器方法
    getQuantityOptions() {
      const options = [];
      const max = 50;
      for (let i = 0.5; i <= max; i += 0.5) {
        options.push(i);
      }
      return options;
    },
    
    openQuantityPicker(product) {
      if (!this.selectedProductIds.includes(product.id)) return;
      this.currentPickerProduct = product;
      this.customQuantity = this.quantities[product.id] || product.minOrderQuantity || 1;
      this.showQuantityPicker = true;
      this.$nextTick(() => {
        const el = this.$refs.modalPickerScroll;
        if (el) {
          const qty = this.quantities[product.id] || product.minOrderQuantity || 1;
          const options = this.getQuantityOptions();
          const index = options.indexOf(qty);
          if (index >= 0) {
            el.scrollTop = index * 32;
          }
        }
      });
    },
    
    closeQuantityPicker() {
      this.showQuantityPicker = false;
      this.currentPickerProduct = null;
    },
    
    onModalPickerScroll() {
      if (this._modalScrollTimer) {
        clearTimeout(this._modalScrollTimer);
      }
      this._modalScrollTimer = setTimeout(() => {
        const el = this.$refs.modalPickerScroll;
        if (!el || !this.currentPickerProduct) return;
        const itemHeight = 32;
        const scrollTop = el.scrollTop;
        const index = Math.round(scrollTop / itemHeight);
        const options = this.getQuantityOptions();
        if (options[index] !== undefined) {
          this.customQuantity = options[index];
        }
      }, 100);
    },
    
    onCustomQuantityInput() {
      const qty = parseFloat(this.customQuantity);
      if (isNaN(qty) || qty <= 0) return;
      const el = this.$refs.modalPickerScroll;
      if (el) {
        const options = this.getQuantityOptions();
        const index = options.indexOf(qty);
        if (index >= 0) {
          el.scrollTop = index * 32;
        }
      }
    },
    
    confirmQuantity() {
      if (this.currentPickerProduct && this.customQuantity > 0) {
        this.$set(this.quantities, this.currentPickerProduct.id, this.customQuantity);
      }
      this.closeQuantityPicker();
    },
    
    // 价格选择器方法
    openPricePicker(product) {
      if (!this.selectedProductIds.includes(product.id)) return;
      this.currentPickerProduct = product;
      this.customPrice = this.prices[product.id] || product.wholesalePrice || product.marketPrice || 0;
      this.showPricePicker = true;
      this.$nextTick(() => {
        const el = this.$refs.pricePickerScroll;
        if (el) {
          el.scrollTop = 99 * 32;
        }
      });
    },
    
    getPriceOptions() {
      const price = Number(this.customPrice) || 0;
      const rawStep = price / 100;
      let step;
      
      if (rawStep <= 0) {
        step = 0.01;
      } else {
        const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
        const firstDigit = Math.floor(rawStep / magnitude);
        step = firstDigit * magnitude;
      }
      
      const options = [];
      for (let i = -99; i <= 100; i++) {
        const optionPrice = price + i * step;
        if (optionPrice >= 0) {
          options.push(parseFloat(optionPrice.toFixed(4)));
        }
      }
      return options;
    },
    
    closePricePicker() {
      this.showPricePicker = false;
      this.currentPickerProduct = null;
    },
    
    onPricePickerScroll() {
      if (this._priceScrollTimer) {
        clearTimeout(this._priceScrollTimer);
      }
      this._priceScrollTimer = setTimeout(() => {
        const el = this.$refs.pricePickerScroll;
        if (!el) return;
        const itemHeight = 32;
        const scrollTop = el.scrollTop;
        const index = Math.round(scrollTop / itemHeight);
        const options = this.getPriceOptions();
        if (options[index] !== undefined) {
          this.customPrice = options[index];
        }
      }, 100);
    },
    
    onCustomPriceInput() {
      const price = parseFloat(this.customPrice);
      if (isNaN(price) || price < 0) return;
      const el = this.$refs.pricePickerScroll;
      if (el) {
        const options = this.getPriceOptions();
        const index = options.findIndex(p => Math.abs(p - price) < 0.01);
        if (index >= 0) {
          el.scrollTop = index * 32;
        }
      }
    },
    
    confirmPrice() {
      if (this.currentPickerProduct && this.customPrice >= 0) {
        const productId = this.currentPickerProduct.id;
        // 确保存储的是数字类型
        const priceValue = Number(this.customPrice) || 0;
        this.$set(this.prices, productId, priceValue);
        // 检查是否改价
        const originalPrice = Number(this.originalPrices[productId]) || 0;
        const isChanged = Math.abs(priceValue - originalPrice) > 0.001;
        this.$set(this.priceChanged, productId, isChanged);
      }
      this.closePricePicker();
    },
    triggerProofUpload() { this.$refs.proofInput.click(); },
    async handleProofUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      try {
        const { uploadFile } = await import('@/services/api');
        const result = await uploadFile('/files/upload-image', file);
        if (result && result.url) this.paymentProofUrl = result.url;
      } catch (e) { console.error(this.$t('driverOnBehalf.uploadProofFailed'), e); }
      finally { event.target.value = ''; }
    },
    async loadPaymentConfig(wholesalerId) {
      try {
        const res = await api.get(`/wholesalers/${wholesalerId}/payment-config`);
        this.paymentConfig = { paynowQrUrl: res?.paynowQrUrl || '', bankName: res?.bankName || '', bankAccountName: res?.bankAccountName || '', bankAccountNumber: res?.bankAccountNumber || '' };
      } catch (e) { this.paymentConfig = { paynowQrUrl: '', bankName: '', bankAccountName: '', bankAccountNumber: '' }; }
    },
    formatValidationErrors(errors) {
      return errors.map(err => {
        if (err.startsWith('PRICE_ERROR:')) {
          const parts = err.split(':');
          return `⚠️ ${parts[1] || this.$t('driverOnBehalf.product')}：${this.$t('driverOnBehalf.priceChanged').replace('{0}', parts[2] || '').replace('{1}', parts[3] || '')}`;
        }
        if (err.startsWith('STOCK_ERROR:')) {
          const parts = err.split(':');
          const p = this.products.find(x => x.id == parts[1]);
          const name = p ? p.name : `${this.$t('driverOnBehalf.product')}${parts[1]}`;
          return `⚠️ ${name}：${this.$t('driverOnBehalf.insufficientStock').replace('{0}', parts[2] || '0').replace('{1}', parts[3] || '')}`;
        }
        return `❌ ${err}`;
      });
    },
    async submitOrder(continueNext) {
      this.validationErrors = [];
      if (!this.selectedCustomer) { this.$message.warning(this.$t('driverOnBehalf.pleaseSelectStall')); return; }
      if (!this.selectedWholesaler) { this.$message.warning(this.$t('driverOnBehalf.pleaseSelectWholesaler')); return; }
      if (this.selectedProductIds.length === 0) { this.$message.warning(this.$t('driverOnBehalf.pleaseSelectProduct')); return; }
      const items = this.orderItems;
      if (items.length === 0) { this.$message.warning(this.$t('driverOnBehalf.pleaseEnterQuantity')); return; }

      this.submitting = true;
      try {
        const validateRes = await api.post('/orders/validate', {
          wholesalerId: this.selectedWholesaler.id,
          customerId: this.selectedCustomer.id,
          items: items.map(i => ({ productId: i.productId, quantity: i.qty, unitPrice: i.price })),
          skipPriceCheck: true,
        });
        if (!validateRes.valid) {
          this.validationErrors = this.formatValidationErrors(validateRes.errors || []);
          this.$message.error(this.$t('driverOnBehalf.orderValidationFailed'));
          return;
        }

        await api.post('/orders', {
          orderType: 'DELIVERY', sourceChannel: 'DRIVER',
          wholesalerId: this.selectedWholesaler.id,
          customerId: this.selectedCustomer.id,
          customerName: this.selectedCustomer.shopName || this.selectedCustomer.customerName || this.selectedCustomer.name,
          customerPhone: this.selectedCustomer.contactPerson || this.selectedCustomer.phone,
          items: items.map(i => ({ productId: i.productId, quantity: i.qty, unitPrice: i.price, priceChanged: i.priceChanged })),
          paymentMethod: this.paymentMethod, paymentStatus: 'paid',
          paymentProofUrl: this.paymentProofUrl || null,
          deliveryMethod: 'DELIVERY',
          deliveryAddress: this.selectedCustomer.address || this.selectedCustomer.companyAddress || '',
          deliveryRegionCode: this.selectedCustomer.regionName || this.selectedCustomer.regionCode || this.selectedCustomer.region || '',
          remark: `${this.$t('driverOnBehalf.title')} - ${new Date().toLocaleString()}`,
          subtotal: this.orderSummary.subtotal, gstAmount: this.orderSummary.gst, totalAmount: this.orderSummary.total,
          skipPriceCheck: true
        });

        this.$message.success(this.$t('driverOnBehalf.orderSuccess'));

        if (continueNext) {
          // 保留档口和批发商选择，只清空商品和订单信息
          this.selectedProductIds = [];
          this.quantities = {};
          this.prices = {};
          this.originalPrices = {};
          this.priceChanged = {};
          this.productSearch = '';
          this.paymentProofUrl = '';
          this.paymentMethod = 'CASH';
          this.validationErrors = [];
          this.products = [];
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          this.$router.push('/delivery/orders');
        }
      } catch (e) {
        const msg = e.response?.data?.message || e.message || '';
        if (msg.includes('PRICE_ERROR:') || msg.includes('STOCK_ERROR:')) {
          this.validationErrors = this.formatValidationErrors([msg]);
        } else if (msg) { this.$message.error(this.$t('driverOnBehalf.orderFailed') + ': ' + msg); }
        else { this.$message.error(this.$t('driverOnBehalf.orderFailed')); }
      } finally { this.submitting = false; }
    }
  }
};
</script>

<style scoped>
/* 滚轮选择器样式 */
.quantity-picker {
  position: relative;
  width: 100%;
  height: 160px;
  overflow: hidden;
  border-radius: 8px;
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  margin-bottom: 12px;
}

.picker-highlight {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 32px;
  transform: translateY(-50%);
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  opacity: 0.15;
  border-radius: 4px;
  pointer-events: none;
  z-index: 2;
}

.picker-scroll {
  height: 100%;
  overflow-y: scroll;
  scroll-snap-type: y mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.picker-scroll::-webkit-scrollbar {
  display: none;
}

.picker-item {
  height: 32px;
  line-height: 32px;
  text-align: center;
  font-size: 16px;
  color: #666;
  scroll-snap-align: center;
  user-select: none;
}

.picker-spacer {
  height: 64px;
  flex-shrink: 0;
}

.driver-order-page { min-height: 100vh; background: #f5f5f5; padding-bottom: 80px; }
.header { background: linear-gradient(135deg, #059669, #10b981); padding: 16px; text-align: center; position: relative; }
.back-btn { position: absolute; left: 16px; top: 16px; color: #fff; cursor: pointer; font-size: 14px; }
.header-title { color: white; font-size: 18px; font-weight: bold; }
.content { padding: 16px; }
.section { background: white; border-radius: 8px; padding: 16px; margin-bottom: 16px; }
.section-title { font-size: 16px; font-weight: bold; color: #333; margin-bottom: 16px; padding-bottom: 8px; border-bottom: 2px solid #059669; display: flex; align-items: center; justify-content: space-between; }
.create-stall-btn { padding: 6px 12px; background: #059669; color: white; border: none; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer; }
.create-stall-btn:active { opacity: 0.8; }
.dialog-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.dialog-box { background: white; border-radius: 12px; width: 85%; max-width: 360px; overflow: hidden; }
.dialog-title { padding: 16px; font-size: 16px; font-weight: bold; color: #333; border-bottom: 1px solid #eee; }
.dialog-content { padding: 20px 16px; }
.dialog-input { width: 100%; padding: 12px; border: 1px solid #e0e0e0; border-radius: 8px; font-size: 14px; outline: none; }
.dialog-input:focus { border-color: #059669; }
.dialog-actions { display: flex; gap: 12px; padding: 0 16px 16px; }
.dialog-cancel-btn, .dialog-confirm-btn { flex: 1; padding: 12px; border: none; border-radius: 8px; font-size: 14px; font-weight: 500; cursor: pointer; }
.dialog-cancel-btn { background: #f5f5f5; color: #666; }
.dialog-confirm-btn { background: #059669; color: white; }
.dialog-confirm-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.section-subtitle { font-size: 13px; font-weight: 600; color: #666; margin-bottom: 8px; }
.payment-hint { background: #ecfdf5; color: #059669; padding: 10px; border-radius: 6px; text-align: center; font-size: 14px; font-weight: 600; margin-bottom: 12px; }
.search-input, .region-select { width: 100%; padding: 10px 14px; border: 1px solid #ddd; border-radius: 6px; font-size: 14px; }
.search-input:focus, .region-select:focus { outline: none; border-color: #059669; }
.filter-box { margin-bottom: 12px; }
.search-box { margin-bottom: 12px; }
.select-all-bar { display: flex; justify-content: space-between; align-items: center; padding: 8px 12px; background: #f8f9fa; border-radius: 6px; margin-bottom: 12px; font-size: 13px; }
.select-all-label { cursor: pointer; }
.select-all-cb { margin-right: 6px; }
.select-count { color: #999; }
.product-item { display: flex; align-items: flex-start; gap: 12px; padding: 12px 0; border-bottom: 1px solid #f0f0f0; }
.product-checkbox { width: 18px; height: 18px; cursor: pointer; margin-top: 8px; }
.product-emoji { font-size: 24px; }
.product-details { flex: 1; }
.product-name { font-weight: bold; color: #333; }
.product-stock { font-size: 12px; color: #666; margin: 4px 0; }
.product-stock .stock-low { color: #ef4444; font-weight: bold; }
.product-stock .stock-warn { color: #f59e0b; font-weight: bold; }
.product-price-ref { font-size: 13px; display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.ref-price { color: #666; font-size: 12px; }
/* 价格编辑框 */
.price-edit-box {
  margin-top: 6px;
  padding: 8px 10px;
  background: #f0fdf4;
  border: 1.5px dashed #059669;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}
.price-edit-box:hover:not(.disabled) {
  background: #ecfdf5;
  border-color: #047857;
  box-shadow: 0 0 0 2px rgba(5, 150, 105, 0.1);
}
.price-edit-box.disabled {
  opacity: 0.4;
  cursor: not-allowed;
  border-style: solid;
  border-color: #e0e0e0;
  background: #f9fafb;
}
.price-label {
  font-size: 11px;
  color: #059669;
  font-weight: 600;
  margin-bottom: 2px;
  display: block;
}
.price-value-row {
  display: flex;
  align-items: center;
  gap: 4px;
}
.price-prefix {
  font-size: 13px;
  color: #666;
  font-weight: 500;
}
.price-value {
  font-size: 18px;
  font-weight: 700;
  color: #ef4444;
}
.edit-icon {
  font-size: 12px;
  margin-left: auto;
}
.unit-label { color: #999; font-size: 12px; }
.price-ref-small {
  font-size: 11px;
  color: #999;
  margin-top: 2px;
}

/* 数量编辑框 */
.quantity-edit-box {
  margin-top: 6px;
  padding: 8px 10px;
  background: #f8fafc;
  border: 1.5px dashed #64748b;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}
.quantity-edit-box:hover:not(.disabled) {
  background: #f1f5f9;
  border-color: #475569;
  box-shadow: 0 0 0 2px rgba(100, 116, 139, 0.1);
}
.quantity-edit-box.disabled {
  opacity: 0.4;
  cursor: not-allowed;
  border-style: solid;
  border-color: #e0e0e0;
  background: #f9fafb;
}
.qty-label {
  font-size: 11px;
  color: #64748b;
  font-weight: 600;
  margin-bottom: 2px;
  display: block;
}
.qty-value-row {
  display: flex;
  align-items: center;
  gap: 4px;
}
.qty-value {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
}
.unit { color: #999; font-size: 12px; min-width: 30px; }

/* 弹窗内自定义输入 */
.custom-input-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}
.custom-input-row label {
  font-size: 13px;
  color: #666;
  flex-shrink: 0;
}
.custom-input {
  width: 80px;
  padding: 6px 10px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 14px;
  text-align: center;
  outline: none;
}
.custom-input:focus {
  border-color: #059669;
}
.input-unit {
  font-size: 13px;
  color: #999;
}

/* 参考价 */
.price-ref-box {
  margin-top: 8px;
  padding: 8px 12px;
  background: #fff7ed;
  border-radius: 6px;
  font-size: 13px;
  color: #666;
}
.price-ref-val {
  font-weight: 600;
  color: #ef4444;
}

/* picker 弹窗加宽 */
.picker-dialog {
  max-width: 380px;
}
.customer-selector { padding: 12px; margin-bottom: 8px; border: 1px solid #eee; border-radius: 6px; cursor: pointer; transition: all 0.2s; }
.customer-selector:hover { background: #f5f5f5; }
.customer-selector.selected { border-color: #059669; background: #ecfdf5; }
.customer-name { font-weight: bold; color: #333; margin-bottom: 4px; }
.customer-info { font-size: 13px; color: #666; }
.wholesaler-list { margin-top: 4px; }
.wholesaler-selector { padding: 12px; margin-bottom: 8px; border: 1px solid #eee; border-radius: 6px; cursor: pointer; transition: all 0.2s; }
.wholesaler-selector:hover { background: #f5f5f5; }
.wholesaler-selector.selected { border-color: #059669; background: #ecfdf5; }
.wholesaler-name { font-weight: bold; color: #333; margin-bottom: 4px; }
.wholesaler-info { font-size: 13px; color: #666; }

/* 档口列表样式 */
.stall-list { margin-top: 4px; }
.stall-selector { padding: 12px; margin-bottom: 8px; border: 1px solid #eee; border-radius: 6px; cursor: pointer; transition: all 0.2s; }
.stall-selector:hover { background: #f5f5f5; }
.stall-selector.selected { border-color: #059669; background: #ecfdf5; }
.stall-name { font-weight: bold; color: #333; margin-bottom: 4px; }
.stall-info { font-size: 13px; color: #666; }
.pagination-bar { display: flex; justify-content: center; align-items: center; gap: 12px; margin-top: 12px; padding: 8px; }
.page-btn { padding: 6px 12px; border: 1px solid #ddd; border-radius: 4px; background: white; cursor: pointer; }
.page-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.page-info { font-size: 13px; color: #666; }
.payment-options { display: flex; gap: 10px; flex-wrap: wrap; }
.payment-option { padding: 12px 20px; border: 2px solid #eee; border-radius: 8px; cursor: pointer; transition: all 0.2s; font-size: 14px; }
.payment-option:hover { border-color: #059669; }
.payment-option.selected { border-color: #059669; background: #ecfdf5; }
.payment-option input { display: none; }
.payment-detail-box { margin-top: 16px; padding: 16px; background: #f9fafb; border-radius: 8px; border: 1px solid #e5e7eb; }
.payment-detail-title { font-size: 15px; font-weight: 600; color: #1f2937; margin-bottom: 12px; }
.qr-display { text-align: center; }
.qr-image { max-width: 240px; max-height: 240px; border-radius: 8px; border: 2px solid #e5e7eb; }
.qr-hint { color: #6b7280; font-size: 13px; margin-top: 8px; }
.qr-empty { text-align: center; color: #ef4444; padding: 16px; background: #fef2f2; border-radius: 6px; }
.proof-upload-area { padding: 20px; background: #f9fafb; border-radius: 8px; border: 2px dashed #d1d5db; text-align: center; cursor: pointer; }
.proof-upload-area:hover { border-color: #059669; background: #ecfdf5; }
.proof-preview { max-width: 100%; max-height: 200px; border-radius: 8px; }
.proof-placeholder { display: flex; flex-direction: column; align-items: center; gap: 8px; }
.proof-icon { font-size: 36px; }
.proof-placeholder p { margin: 0; font-size: 13px; color: #6b7280; }
.summary-row { display: flex; justify-content: space-between; padding: 8px 0; font-size: 13px; border-bottom: 1px dashed #f0f0f0; }
.line-item-summary { display: flex; align-items: center; gap: 8px; padding: 6px 0; font-size: 12px; border-bottom: 1px dotted #eee; }
.line-name { flex: 1; color: #333; font-weight: 500; }
.line-qty { color: #666; }
.line-price { color: #f59e0b; font-weight: 600; min-width: 65px; text-align: right; }
.line-tax { color: #059669; min-width: 70px; text-align: right; }
.summary-row.total { border-top: 2px solid #059669; border-bottom: none; padding-top: 12px; margin-top: 8px; }
.summary-row .label { color: #666; }
.summary-row .value { font-weight: 600; color: #333; }
.summary-row.total .label { font-size: 15px; font-weight: 700; color: #1e293b; }
.summary-row.total .value { font-size: 18px; font-weight: 700; color: #059669; }
.error-section { border: 2px solid #ef4444; background: #fef2f2; }
.error-section .section-title { color: #dc2626; border-bottom-color: #ef4444; }
.error-item { padding: 8px 12px; margin: 4px 0; background: rgba(255,255,255,0.6); border-radius: 4px; font-size: 13px; color: #991b1b; }
.footer { padding: 16px; border-top: 1px solid #eee; display: flex; gap: 12px; justify-content: center; background: white; border-radius: 8px; margin-top: 16px; }
.submit-btn, .submit-continue-btn { padding: 12px 32px; border: none; border-radius: 6px; font-size: 15px; font-weight: bold; cursor: pointer; }
.submit-btn { background: #059669; color: white; }
.submit-btn:hover:not(:disabled) { background: #047857; }
.submit-continue-btn { background: #3b82f6; color: white; }
.submit-continue-btn:hover:not(:disabled) { background: #2563eb; }
.submit-btn:disabled, .submit-continue-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.loading-state { text-align: center; padding: 40px; color: #666; }
.loading-spinner { font-size: 32px; margin-bottom: 8px; }
.empty-state { padding: 40px; text-align: center; color: #999; }

/* 商品搜索下拉 */
.product-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-top: 4px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 500;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}
.product-option {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f5f5f5;
}
.product-option:last-child { border-bottom: none; }
.product-option:active { background: #f0fdf4; }
.option-name { font-size: 14px; color: #333; font-weight: 500; flex: 1; }
.option-sku { font-size: 12px; color: #999; margin-left: 8px; }

/* 已选商品区域 */
.selected-products-section {
  background: #f0fdf4;
  border: 1px solid #a7f3d0;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
}
.selected-title { font-size: 14px; font-weight: 600; color: #059669; margin-bottom: 8px; }
.selected-products-list { display: flex; flex-direction: column; gap: 6px; }
.selected-product-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px;
  background: white;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}
.selected-product-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}
.selected-product-name {
  font-size: 13px;
  color: #333;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.selected-product-price { font-size: 12px; color: #059669; font-weight: 600; }
.btn-remove-selected {
  background: #fee2e2;
  color: #dc2626;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;
  flex-shrink: 0;
  margin-left: 8px;
}
.btn-remove-selected:active { background: #fecaca; }
</style>
